import NewActivityCard from '@alltrails/shared/components/ActivityCard';
import MapCard from '@alltrails/shared/components/MapCard';
import Track from '@alltrails/shared/types/track';
import { useIntl } from '@alltrails/shared/react-intl';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { getActivityFromTrack, getMapFromTrack } from 'utils/cardInfo';
import { Context } from '@alltrails/shared/types/context';
import useActivityDuration from 'hooks/useActivityDuration';
import useAdminFooterCTAs, { ButtonConfig } from 'hooks/useAdminFooterCTAs';
import CustomProvider from 'components/CustomProvider';
import useExperiment from 'hooks/useExperiment';

type ActivityHoverCardProps = {
  track: Track;
  context: Context;
  handleCardClick: () => void;
  handleFavoriteClick: () => void;
  isFavorite?: boolean;
  handleMouseEnter?: () => void;
  handleMouseLeave?: () => void;
  bottomButtonsConfig?: ButtonConfig[];
};

const BaseActivityHoverCard = ({
  track,
  context,
  handleCardClick,
  handleFavoriteClick,
  isFavorite,
  handleMouseEnter,
  handleMouseLeave,
  bottomButtonsConfig
}: ActivityHoverCardProps): JSX.Element => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const minutes = useActivityDuration(track);
  const footerCTAs = useAdminFooterCTAs(bottomButtonsConfig, intl);
  const experiment = useExperiment('web-feed-polyline-color');
  // TODO: delete feature flag - https://alltrails.atlassian.net/browse/CMTY-4163
  const newPolylineColorsFlag = experiment?.value === 'on';

  const card =
    track.type === 'map' ? (
      <MapCard
        languageRegionCode={languageRegionCode}
        onCardClick={handleCardClick}
        onCardMouseEnter={handleMouseEnter}
        onCardMouseLeave={handleMouseLeave}
        onFavoriteClick={handleFavoriteClick}
        isFavorite={isFavorite}
        variant="small"
        map={getMapFromTrack(track, minutes, languageRegionCode)}
        isFirstParty={context?.currentUser?.id === track.user?.id}
        useMetric={context?.displayMetric}
        footerCTAs={footerCTAs && footerCTAs.length ? footerCTAs : undefined}
        useV3api={newPolylineColorsFlag}
      />
    ) : (
      <NewActivityCard
        languageRegionCode={languageRegionCode}
        onCardClick={handleCardClick}
        onCardMouseEnter={handleMouseEnter}
        onCardMouseLeave={handleMouseLeave}
        onFavoriteClick={handleFavoriteClick}
        isFavorite={isFavorite}
        variant="small"
        activity={getActivityFromTrack(track, minutes)}
        isFirstParty={context?.currentUser?.id === track.user?.id}
        useMetric={context?.displayMetric}
        footerCTAs={footerCTAs && footerCTAs.length ? footerCTAs : undefined}
        showActivityType={context?.currentUser?.admin}
        useV3api={newPolylineColorsFlag}
      />
    );

  return card;
};

export default function ActivityHoverCard({
  track,
  context,
  handleCardClick,
  handleFavoriteClick,
  isFavorite,
  handleMouseEnter,
  handleMouseLeave,
  bottomButtonsConfig
}: ActivityHoverCardProps) {
  return (
    <CustomProvider>
      <BaseActivityHoverCard
        track={track}
        context={context}
        handleCardClick={handleCardClick}
        handleFavoriteClick={handleFavoriteClick}
        isFavorite={isFavorite}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        bottomButtonsConfig={bottomButtonsConfig}
      />
    </CustomProvider>
  );
}
